import React, {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo
} from 'react'

import {
  DefaultValues,
  FieldErrors,
  FieldValues,
  FormProvider,
  useForm,
  UseFormGetValues,
  UseFormReset,
  UseFormSetValue
} from 'react-hook-form'

import { DirtyContext } from './DirtyProvider'
import { CustomContext } from './CustomProvider'

export interface RefType<T extends FieldValues> {
  reset: UseFormReset<T>
  setValue: UseFormSetValue<T>
  getValues: UseFormGetValues<T>
}

export type Props<T extends FieldValues> = React.PropsWithChildren<{
  defaultValues?: DefaultValues<T>
  values?: T
  onSubmit: (form: T) => Promise<void> | void
  readOnly?: boolean
  disabled?: boolean
  serverErrors?: FieldErrors<T>
  subscribe?: (data: T) => void
  ref?: RefObject<RefType<T> | null>
  formRef?: RefObject<HTMLFormElement | null>
  onSubmittedSuccessfully?: () => void
  overrideClassName?: string
  generateDefaultValues?: () => DefaultValues<T>
}>

export interface ServerError {
  type: string
  message: string
}

export function Root<T extends FieldValues>(props: Props<T>) {
  const {
    children,
    defaultValues,
    values,
    serverErrors,
    readOnly = false,
    disabled = false,
    ref,
    formRef,
    overrideClassName,
    subscribe,
    onSubmit,
    onSubmittedSuccessfully,
    generateDefaultValues
  } = props

  const id = useId()
  const { setIsDirty } = useContext(DirtyContext)

  const { reset, watch, formState, setValue, getValues, ...methods } =
    useForm<T>({
      disabled,
      errors: serverErrors,
      defaultValues,
      values,
      shouldFocusError: true
    })

  const onSubmitProxy = useCallback(
    async (data: T) => {
      await Promise.resolve(onSubmit(data)).catch(error => console.error(error))
    },
    [onSubmit]
  )

  useEffect(() => {
    if (!formState.isSubmitSuccessful) return

    reset(generateDefaultValues ? generateDefaultValues() : defaultValues)
    onSubmittedSuccessfully?.()
  }, [
    formState.isSubmitSuccessful,
    reset,
    defaultValues,
    onSubmittedSuccessfully,
    generateDefaultValues
  ])

  useEffect(() => {
    if (!subscribe) return

    const { unsubscribe } = watch(values => {
      subscribe(values as T)
    }, defaultValues)

    return () => unsubscribe()
  }, [defaultValues, watch, subscribe])

  useImperativeHandle(ref, () => {
    return {
      getValues,
      reset,
      setValue
    }
  }, [reset, setValue, getValues])

  useEffect(() => {
    setIsDirty(id, formState.isDirty)

    return () => setIsDirty(id, false)
  }, [id, formState.isDirty, setIsDirty])

  const customContextValue = useMemo(
    () => ({
      readOnly,
      disabled
    }),
    [readOnly, disabled]
  )

  return (
    <FormProvider
      {...methods}
      reset={reset}
      watch={watch}
      setValue={setValue}
      getValues={getValues}
      formState={formState}
    >
      <CustomContext value={customContextValue}>
        <form
          className={overrideClassName ?? 'flex flex-col gap-2.5'}
          ref={formRef}
          aria-readonly={readOnly}
          onSubmit={e => {
            e.preventDefault()
            e.stopPropagation()

            if (readOnly) return

            methods.handleSubmit(onSubmitProxy)(e)
          }}
        >
          {children}
        </form>
      </CustomContext>
    </FormProvider>
  )
}
